import { v4 } from "uuid";

import type { CuratedPlaceOfSafety } from "@thalamos/common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  type CreateIncidentMinimumFields,
  type ExtendedVisionUser,
  type IncidentFormId,
  type IncidentFormType,
  type IncidentWorkItemId,
  type QuickCreateSampleData,
  type WorkflowVersion,
} from "@thalamos/vision-common";

import api from "../api/index.js";
import { config } from "../config/index.js";
import { generateDemoDispatchNumber } from "./quickCreateSampleData.js";

/**
 * Quick create fabricates statutory records that no officer signed off, so it
 * must never be reachable in production.
 */
export const isQuickCreateEnabled = [
  "local",
  "dev",
  "docker",
  "staging",
].includes(config.environment);

/**
 * Whether the element takes text entry, so keystrokes there are input rather
 * than a shortcut.
 */
export function isTypingInto(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  return (
    element.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName)
  );
}

/**
 * A random curated place of safety, or `undefined` when there is none to pick
 * from, so the caller's sample data fabricates one instead.
 */
export async function pickRandomPlaceOfSafety({
  posType,
}: {
  posType?: CuratedPlaceOfSafety["posType"];
} = {}): Promise<CuratedPlaceOfSafety | undefined> {
  try {
    const response = await api.placeOfSafety.getAll();
    const allPlacesOfSafety: readonly CuratedPlaceOfSafety[] =
      response.data?.placesOfSafety ?? [];
    const placesOfSafety = allPlacesOfSafety.filter(
      (placeOfSafety) =>
        posType === undefined || placeOfSafety.posType === posType,
    );
    return placesOfSafety.length > 0
      ? placesOfSafety[Math.floor(Math.random() * placesOfSafety.length)]
      : undefined;
  } catch {
    console.warn(
      "Quick create: failed to fetch places of safety, using fallback",
    );
    return undefined;
  }
}

export type QuickCreateContext = {
  sessionContext: NonNullable<ExtendedVisionUser["sessionContext"]>;
  workflowVersion: WorkflowVersion;
};

/**
 * Throws when the user has no session context or their organisation has no
 * workflow version configured.
 */
export function quickCreateContextOf(
  user: ExtendedVisionUser,
): QuickCreateContext {
  const sessionContext = user.sessionContext;
  if (!sessionContext) {
    throw new Error("No user session context available");
  }

  const workflowVersion =
    user.sessionOrganisationConfiguration?.workflowVersion;
  if (workflowVersion === undefined) {
    throw new Error("Current organisation has no workflow version configured");
  }

  return { sessionContext, workflowVersion };
}

/**
 * Creates an incident and submits its triage, then returns the id of the
 * `formType` form triage created. Throws on the first failed step.
 */
export async function createTriagedIncident({
  context: { sessionContext, workflowVersion },
  sampleData,
  formType,
}: {
  context: QuickCreateContext;
  sampleData: Pick<QuickCreateSampleData, "createIncidentMain" | "triageMain">;
  formType: IncidentFormType;
}): Promise<{
  incidentWorkItemId: IncidentWorkItemId;
  formId: IncidentFormId;
}> {
  const incidentWorkItemId = IncidentWorkItemIdSchema.parse(v4());

  // Sample data is validated against workflow schemas by tests in vision-common.
  // Its commandUnit is a placeholder uuid, so the session organisation replaces it.
  const createIncidentFormSection = {
    ...sampleData.createIncidentMain,
    dispatchNumber: { value: generateDemoDispatchNumber() },
    commandUnit: {
      value: sessionContext.organisationId,
      valueLabel: sessionContext.organisationName,
    },
  };
  const createResponse = await api.incidentWorkItem.create(incidentWorkItemId, {
    workflowVersion,
    // Sample sections are loosely typed; the server validates the section
    // against the workflow's own schema.
    createIncidentFormSection:
      createIncidentFormSection as unknown as CreateIncidentMinimumFields,
  });
  if (createResponse.status !== 204) {
    throw new Error(
      `Failed to create incident: ${String(createResponse.status)}`,
    );
  }

  const triageFormId = await formIdOfType({
    incidentWorkItemId,
    formType: IncidentFormTypeSchema.enum.triage,
  });
  const triageResponse = await api.incidentWorkItem.submitTriage(
    incidentWorkItemId,
    triageFormId,
    FormSectionSchema.enum.triageMain,
    sampleData.triageMain,
  );
  if (triageResponse.status !== 204) {
    throw new Error(
      `Failed to submit triage: ${String(triageResponse.status)}`,
    );
  }

  const formId = await formIdOfType({ incidentWorkItemId, formType });
  return { incidentWorkItemId, formId };
}

async function formIdOfType({
  incidentWorkItemId,
  formType,
}: {
  incidentWorkItemId: IncidentWorkItemId;
  formType: IncidentFormType;
}): Promise<IncidentFormId> {
  const response = await api.incidentWorkItem.get(incidentWorkItemId);
  const form = response.data?.forms.find((f) => f.formType === formType);
  if (!form) {
    throw new Error(`${formType} form not found`);
  }
  return form.id;
}
